/**
 * Client ของ "ข้อยกเว้นการรับของ" — พูดกับ `GET /v1/receiving-exceptions`,
 * `GET /v1/receiving-exceptions/summary` และ `POST
 * /v1/receiving-discrepancies/{id}/approve` ตาม payload contract ที่ freeze ไว้
 * (D-168 B6, D-091)
 *
 * หลักการเดียวกับ `lib/goodsReceipts/client.ts` และ `lib/procurement/client.ts`:
 * - ฟังก์ชันในไฟล์นี้ **ไม่ throw** ความล้มเหลวทุกแบบกลายเป็นสถานะที่เรียกดูได้
 * - payload ที่ผิดรูปคือ `invalid-response` ไม่ใช่ "คิวว่าง" — รายการว่างกับโหลดไม่
 *   สำเร็จต้องแยกกันให้ขาด ไม่งั้นจัดซื้อจะอ่านว่าไม่มีข้อยกเว้นทั้งที่โหลดพัง
 * - **ไม่มี fallback ที่แต่งข้อมูลขึ้นมาเอง** และ **ไม่เขียนอะไรลง storage เลย**
 * - ทุกคำขอเดินผ่าน `apiFetch` ตัวเดียวกับ client อื่น — header
 *   `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว ไม่ใช่ที่นี่
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ
 * (`references/authorization-rbac.md` ข้อ 5)
 */

import type {
  ApprovedReceivingDiscrepancy,
  ReceivingExceptionItem,
  ReceivingExceptionListPage,
  ReceivingExceptionListQuery,
  ReceivingExceptionSummary,
  ReceivingExceptionSummaryQuery,
  ReceivingExceptionSummaryRow,
  ReceivingExceptionSummaryTotals,
} from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

export const RECEIVING_EXCEPTIONS_PATH = '/v1/receiving-exceptions';
export const RECEIVING_EXCEPTIONS_SUMMARY_PATH = `${RECEIVING_EXCEPTIONS_PATH}/summary`;
export const RECEIVING_DISCREPANCIES_PATH = '/v1/receiving-discrepancies';

/**
 * เส้นทางของการอนุมัติ — `discrepancy_id` เป็น UUID surrogate (D-039)
 *
 * `encodeURIComponent` ด้วยเหตุผลเดียวกับทุก client อื่น: ค่าที่อ่านมาจาก payload
 * ของเซิร์ฟเวอร์เองแล้วแปะลง URL ดิบ ๆ คือวิธีที่ `POST` จะไปโผล่ที่เส้นอื่นในวันที่
 * payload ไม่ใช่สิ่งที่คิดไว้
 */
export function approveReceivingDiscrepancyPath(discrepancyId: string): string {
  return `${RECEIVING_DISCREPANCIES_PATH}/${encodeURIComponent(discrepancyId)}/approve`;
}

/** Envelope ของ error ตาม `api/errors.py` (`error_code`, `message`, `details`) */
export interface ApiErrorEnvelope {
  readonly error_code: string | null;
  readonly message: string | null;
  /** `details.rule` — วิธีที่ contract แยกกรณีทางธุรกิจออกจากกัน */
  readonly rule: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

const EMPTY_ENVELOPE: ApiErrorEnvelope = {
  error_code: null,
  message: null,
  rule: null,
  details: {},
};

/**
 * ความล้มเหลวทุกแบบที่ contract นิยามไว้ แยกเป็นสถานะของตัวเอง
 *
 * `rule-violation` (`422`) เป็นที่อยู่ของทั้งช่วงวันที่ของ `summary` ที่กว้างเกินไป
 * และกฎแบ่งแยกหน้าที่ของ `approve` (`APPROVER_MUST_DIFFER_FROM_RAISER`/
 * `SOD_REOPENER_CANNOT_APPROVE`) ส่วน `conflict` (`409`) คือแถวที่ไม่อยู่ในสถานะที่
 * อนุมัติได้อีกแล้ว (`RECEIVING_DISCREPANCY_NOT_APPROVABLE`) — คนละเรื่องกันและ
 * คนละทางแก้: กฎแบ่งแยกหน้าที่ต้องให้คนอื่นกด ส่วนสถานะเปลี่ยนไปแล้วต้องโหลดคิวใหม่
 */
export type ReceivingExceptionFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'not-found'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'conflict'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'rule-violation'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'bad-request'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export type ReceivingExceptionListResult =
  | { readonly status: 'ready'; readonly page: ReceivingExceptionListPage }
  | ReceivingExceptionFailure;

export type ReceivingExceptionSummaryResult =
  | { readonly status: 'ready'; readonly summary: ReceivingExceptionSummary }
  | ReceivingExceptionFailure;

export type ApproveReceivingDiscrepancyResult =
  | { readonly status: 'ready'; readonly discrepancy: ApprovedReceivingDiscrepancy }
  | ReceivingExceptionFailure;

export interface ReceivingExceptionRequestOptions {
  /** base path ของ API (default: อ่านจาก `VITE_API_BASE_URL` แบบ same-origin) */
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  /**
   * inject ได้เพื่อการทดสอบ — production ใช้ `apiFetch` ตัวเดียวกับ client อื่น
   * (header `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว ไม่ใช่ที่นี่)
   */
  readonly fetchImpl?: typeof fetch;
}

/* --------------------------------------------------------------- query ---- */

/**
 * ต่อ query string ของ `GET /v1/receiving-exceptions` — เฉพาะตัวกรองที่เลือกไว้
 *
 * `include_closed` ส่งเฉพาะตอนเป็น `true` — ค่าเริ่มต้นฝั่งเซิร์ฟเวอร์คือ `false`
 * อยู่แล้ว การส่ง `include_closed=false` ซ้ำไม่ได้ประโยชน์อะไรและเพิ่มพารามิเตอร์
 * ที่ไม่มีใครต้องอ่าน
 */
export function buildReceivingExceptionsQuery(
  query: ReceivingExceptionListQuery = {},
): string {
  const params = new URLSearchParams();
  const append = (key: string, value: string | undefined) => {
    if (typeof value === 'string' && value.trim() !== '') {
      params.append(key, value.trim());
    }
  };

  append('plant_code', query.plant_code);
  if (query.include_closed === true) {
    params.append('include_closed', 'true');
  }
  append('discrepancy_type', query.discrepancy_type);
  append('supplier_code', query.supplier_code);
  append('source', query.source);
  append('date_from', query.date_from);
  append('date_to', query.date_to);
  if (typeof query.limit === 'number' && Number.isInteger(query.limit)) {
    params.append('limit', String(query.limit));
  }
  append('cursor', query.cursor);

  const serialized = params.toString();
  return serialized === '' ? '' : `?${serialized}`;
}

/** ต่อ query string ของ `GET /v1/receiving-exceptions/summary` — `date_from`/`date_to` บังคับ */
export function buildReceivingExceptionsSummaryQuery(
  query: ReceivingExceptionSummaryQuery,
): string {
  const params = new URLSearchParams();
  params.append('date_from', query.date_from);
  params.append('date_to', query.date_to);
  const append = (key: string, value: string | undefined) => {
    if (typeof value === 'string' && value.trim() !== '') {
      params.append(key, value.trim());
    }
  };
  append('plant_code', query.plant_code);
  append('supplier_code', query.supplier_code);
  return `?${params.toString()}`;
}

/* -------------------------------------------------------------- parsing -- */

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** ฟิลด์ที่ nullable ได้ — `undefined` แปลว่า "ผิดรูป" (คีย์ที่หายไปทั้งคีย์ถือเป็น `null`) */
function nullableString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

/**
 * `next_cursor` ของ envelope — cursor ว่างไม่ใช่ "มีหน้าถัดไป" ปุ่มโหลดเพิ่มที่ส่ง
 * cursor ว่างจะวนขอหน้าเดิมซ้ำไปเรื่อย ๆ
 */
function parseCursor(raw: unknown): string | null | undefined {
  const cursor = nullableString(raw);
  if (cursor === undefined) {
    return undefined;
  }
  return cursor === null || cursor.trim() === '' ? null : cursor;
}

/**
 * หนึ่งแถวของคิว — **บังคับครบทุกฟิลด์ที่ contract บังคับ** ตามรูปที่
 * `modules/inventory/receiving_exceptions.py` ตอบจริง
 */
export function parseReceivingExceptionItem(raw: unknown): ReceivingExceptionItem | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const discrepancyId = requiredString(record.discrepancy_id);
  const plantCode = requiredString(record.plant_code);
  const poId = requiredString(record.po_id);
  const poNumber = requiredString(record.po_number);
  const supplierCode = requiredString(record.supplier_code);
  const supplierName = requiredString(record.supplier_name);
  const productCode = requiredString(record.product_code);
  const productName = requiredString(record.product_name);
  const discrepancyType = requiredString(record.discrepancy_type);
  const status = requiredString(record.status);
  const expectedQty = requiredString(record.expected_qty);
  const actualQty = requiredString(record.actual_qty);
  const source = requiredString(record.source);
  const createdAt = requiredString(record.created_at);

  const poLineNo = record.po_line_no;
  if (typeof poLineNo !== 'number' || !Number.isInteger(poLineNo)) {
    return null;
  }

  if (
    discrepancyId === null ||
    plantCode === null ||
    poId === null ||
    poNumber === null ||
    supplierCode === null ||
    supplierName === null ||
    productCode === null ||
    productName === null ||
    discrepancyType === null ||
    status === null ||
    expectedQty === null ||
    actualQty === null ||
    source === null ||
    createdAt === null
  ) {
    return null;
  }

  if (typeof record.closure_reopened !== 'boolean') {
    return null;
  }

  const value = nullableString(record.value);
  const reasonCode = nullableString(record.reason_code);
  const reason = nullableString(record.reason);
  const reportedByRef = nullableString(record.reported_by_ref);
  const raisedByEmployeeId = nullableString(record.raised_by_employee_id);
  const approvedByEmployeeId = nullableString(record.approved_by_employee_id);
  const approvedAt = nullableString(record.approved_at);

  if (
    value === undefined ||
    reasonCode === undefined ||
    reason === undefined ||
    reportedByRef === undefined ||
    raisedByEmployeeId === undefined ||
    approvedByEmployeeId === undefined ||
    approvedAt === undefined
  ) {
    return null;
  }

  return {
    discrepancy_id: discrepancyId,
    plant_code: plantCode,
    po_id: poId,
    po_number: poNumber,
    po_line_no: poLineNo,
    supplier_code: supplierCode,
    supplier_name: supplierName,
    product_code: productCode,
    product_name: productName,
    discrepancy_type: discrepancyType,
    status,
    expected_qty: expectedQty,
    actual_qty: actualQty,
    value,
    source,
    reason_code: reasonCode,
    reason,
    reported_by_ref: reportedByRef,
    closure_reopened: record.closure_reopened,
    raised_by_employee_id: raisedByEmployeeId,
    approved_by_employee_id: approvedByEmployeeId,
    approved_at: approvedAt,
    created_at: createdAt,
  };
}

/** อ่านรายการจาก envelope `{ items, next_cursor }` — และจากที่นั่นเท่านั้น */
export function parseReceivingExceptionListPage(
  raw: unknown,
): ReceivingExceptionListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }

  const items: ReceivingExceptionItem[] = [];
  for (const entry of record.items) {
    const parsed = parseReceivingExceptionItem(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = parseCursor(record.next_cursor);
  return cursor === undefined ? null : { items, next_cursor: cursor };
}

function parseSummaryRow(raw: unknown): ReceivingExceptionSummaryRow | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const month = requiredString(record.month);
  const plantCode = requiredString(record.plant_code);
  const supplierCode = requiredString(record.supplier_code);
  const supplierName = requiredString(record.supplier_name);
  const discrepancyType = requiredString(record.discrepancy_type);
  const value = requiredString(record.value);
  const count = record.count;

  if (
    month === null ||
    plantCode === null ||
    supplierCode === null ||
    supplierName === null ||
    discrepancyType === null ||
    value === null ||
    typeof count !== 'number' ||
    !Number.isInteger(count)
  ) {
    return null;
  }

  const reasonCode = nullableString(record.reason_code);
  if (reasonCode === undefined) {
    return null;
  }

  return {
    month,
    plant_code: plantCode,
    supplier_code: supplierCode,
    supplier_name: supplierName,
    discrepancy_type: discrepancyType,
    reason_code: reasonCode,
    count,
    value,
  };
}

function parseSummaryTotals(raw: unknown): ReceivingExceptionSummaryTotals | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const exceptions = record.exceptions;
  const lateDeliveries = record.late_deliveries;
  const ordersClosedShort = record.orders_closed_short;
  const shortValue = requiredString(record.short_value);
  const overValue = requiredString(record.over_value);

  if (
    typeof exceptions !== 'number' ||
    !Number.isInteger(exceptions) ||
    typeof lateDeliveries !== 'number' ||
    !Number.isInteger(lateDeliveries) ||
    typeof ordersClosedShort !== 'number' ||
    !Number.isInteger(ordersClosedShort) ||
    shortValue === null ||
    overValue === null
  ) {
    return null;
  }

  return {
    exceptions,
    short_value: shortValue,
    over_value: overValue,
    late_deliveries: lateDeliveries,
    orders_closed_short: ordersClosedShort,
  };
}

/** `GET /v1/receiving-exceptions/summary` — `{date_from, date_to, rows, totals}` */
export function parseReceivingExceptionSummary(raw: unknown): ReceivingExceptionSummary | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const dateFrom = requiredString(record.date_from);
  const dateTo = requiredString(record.date_to);
  if (dateFrom === null || dateTo === null || !Array.isArray(record.rows)) {
    return null;
  }

  const rows: ReceivingExceptionSummaryRow[] = [];
  for (const entry of record.rows) {
    const parsed = parseSummaryRow(entry);
    if (parsed === null) {
      return null;
    }
    rows.push(parsed);
  }

  const totals = parseSummaryTotals(record.totals);
  if (totals === null) {
    return null;
  }

  return { date_from: dateFrom, date_to: dateTo, rows, totals };
}

/**
 * ผลของ `approve` — อ่านเฉพาะสี่ฟิลด์ที่หน้าจอนี้ใช้ต่อ (ดูเหตุผลที่
 * `ApprovedReceivingDiscrepancy` ใน `types.ts`) แม้เซิร์ฟเวอร์จะตอบ
 * `ReceivingDiscrepancyResponse` เต็มรูปก็ตาม
 */
export function parseApprovedReceivingDiscrepancy(
  raw: unknown,
): ApprovedReceivingDiscrepancy | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const discrepancyId = requiredString(record.discrepancy_id);
  const status = requiredString(record.status);
  if (discrepancyId === null || status === null) {
    return null;
  }

  const approvedByEmployeeId = nullableString(record.approved_by_employee_id);
  const approvedAt = nullableString(record.approved_at);
  if (approvedByEmployeeId === undefined || approvedAt === undefined) {
    return null;
  }

  return {
    discrepancy_id: discrepancyId,
    status,
    approved_by_employee_id: approvedByEmployeeId,
    approved_at: approvedAt,
  };
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  if (typeof raw !== 'object' || raw === null) {
    return EMPTY_ENVELOPE;
  }
  const record = raw as Record<string, unknown>;
  const details =
    typeof record.details === 'object' && record.details !== null
      ? (record.details as Record<string, unknown>)
      : {};

  return {
    error_code: requiredString(record.error_code),
    message: requiredString(record.message),
    rule: requiredString(details.rule),
    details,
  };
}

/* ------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    // error body ที่อ่านไม่ออกไม่ควรกลบสถานะ HTTP ที่อ่านออกแล้ว
    return EMPTY_ENVELOPE;
  }
}

type PayloadResult =
  | { readonly status: 'payload'; readonly payload: unknown }
  | ReceivingExceptionFailure;

async function requestPayload(
  path: string,
  init: RequestInit,
  options: ReceivingExceptionRequestOptions,
): Promise<PayloadResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

/** `GET /v1/receiving-exceptions` — กรองตามขอบเขตโรงงานของผู้เรียกเสมอ (PROCUREMENT + VIEW) */
export async function listReceivingExceptions(
  query: ReceivingExceptionListQuery = {},
  options: ReceivingExceptionRequestOptions = {},
): Promise<ReceivingExceptionListResult> {
  const result = await requestPayload(
    `${RECEIVING_EXCEPTIONS_PATH}${buildReceivingExceptionsQuery(query)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parseReceivingExceptionListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/** `GET /v1/receiving-exceptions/summary` — `date_from`/`date_to` บังคับ (ช่วงเกิน 366 วันได้ 422) */
export async function getReceivingExceptionSummary(
  query: ReceivingExceptionSummaryQuery,
  options: ReceivingExceptionRequestOptions = {},
): Promise<ReceivingExceptionSummaryResult> {
  const result = await requestPayload(
    `${RECEIVING_EXCEPTIONS_SUMMARY_PATH}${buildReceivingExceptionsSummaryQuery(query)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const summary = parseReceivingExceptionSummary(result.payload);
  return summary === null ? { status: 'invalid-response' } : { status: 'ready', summary };
}

/**
 * `POST /v1/receiving-discrepancies/{id}/approve` — `INVENTORY` + `APPROVE`
 *
 * ไม่มี body: ผู้อนุมัติมาจาก principal ของผู้เรียก ไม่ใช่ค่าที่ client ส่งไปเลือกเอง
 * **ผู้บันทึกเองหรือผู้ที่เปิด PO กลับหลัง short-close กดแล้วไม่สำเร็จ** และไม่มีอะไร
 * ถูกเขียน (`422 APPROVER_MUST_DIFFER_FROM_RAISER`/`SOD_REOPENER_CANNOT_APPROVE`)
 */
export async function approveReceivingDiscrepancy(
  discrepancyId: string,
  options: ReceivingExceptionRequestOptions = {},
): Promise<ApproveReceivingDiscrepancyResult> {
  const result = await requestPayload(
    approveReceivingDiscrepancyPath(discrepancyId),
    { method: 'POST', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const discrepancy = parseApprovedReceivingDiscrepancy(result.payload);
  return discrepancy === null
    ? { status: 'invalid-response' }
    : { status: 'ready', discrepancy };
}

/**
 * อ่าน `details.rule` จากความล้มเหลวที่มี envelope — คืน `null` เมื่อไม่มี
 *
 * ไม่เดาค่าเมื่ออ่านไม่ได้ เพราะกฎที่เดาผิดจะส่งผู้ใช้ไปแก้เรื่องที่ไม่ได้เสีย
 */
export function ruleFromFailure(failure: ReceivingExceptionFailure): string | null {
  return 'error' in failure ? failure.error.rule : null;
}

/** อ่าน `details.current_status` จากความล้มเหลว — สถานะที่แถวนั้น **ถืออยู่จริง** */
export function currentStatusFromFailure(failure: ReceivingExceptionFailure): string | null {
  if (!('error' in failure)) {
    return null;
  }
  const value = failure.error.details.current_status;
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}
